import React, { useEffect, useState } from "react";
import axiosClient from "../../../axios-client";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUpload,
  faFileDownload,
  faEdit,
  faSearch,
  faTrash,
  faSortAlphaDown,
  faSortAlphaUp,
  faArrowUpAZ,
  faArrowUpZA,
} from "@fortawesome/free-solid-svg-icons";
import Navbar from "../Navbar";
import SideBar from "../SideBar";
import { useNavigate } from "react-router-dom";

const Editreport = () => {
  const [file, setFile] = useState(null);
  const [newTitle, setNewTitle] = useState(""); // State for updated title
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [reports, setReports] = useState([]); // All reports
  const [filteredReports, setFilteredReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [segmentModalOpen, setSegmentModalOpen] = useState(false); // For segment update modal
  const [selectedReport, setSelectedReport] = useState(null); // Stores the selected report object
  const [searchTerm, setSearchTerm] = useState(""); // Search term for filtering
  const [deleteModalOpen, setDeleteModalOpen] = useState(false); // State for delete confirmation modal
  const [reportToDelete, setReportToDelete] = useState(null); // State for the report to delete
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Number of items per page
  const [sortOrder, setSortOrder] = useState("asc");
  const navigate = useNavigate(); // Initialize the navigate function
  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const response = await axiosClient.get("/reports/getReport");
        setReports(response.data); // Save full report objects
        setFilteredReports(response.data); // Initialize filteredReports
      } catch (error) {
        console.error("Error fetching reports:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, []);

  const handleUpdate = async (event) => {
    event.preventDefault();
    if (!file || !selectedReport) {
      setMessage("Please select a file to upload.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      setUploading(true);
      const response = await axiosClient.put(
        `reports/upload/reportdata/${selectedReport._id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setMessage(`Report updated successfully: ${response.data.message}`);
      setModalOpen(false); // Close the regular modal
      setFile(null); // Reset file input
    } catch (error) {
      console.error("Update error:", error);
      setMessage("Failed to update the report. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    const adminToken = localStorage.getItem("Admin_Token");
    if (!adminToken) {
      navigate("/admin/login");
    }
  }, [navigate]);

  // Handle file upload for updating a segment of an existing report
  const handleSegmentUpdate = async (event) => {
    event.preventDefault();
    if (!file || !selectedReport) {
      setMessage("Please select a file to upload.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      setUploading(true);
      const response = await axiosClient.put(
        `/reports/upload/segmentdata/${selectedReport._id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setMessage(`Segment updated successfully: ${response.data.message}`);
      setSegmentModalOpen(false); // Close the segment update modal
      setFile(null); // Reset file input
    } catch (error) {
      console.error("Segment update error:", error);
      setMessage("Failed to update the segment. Please try again.");
    } finally {
      setUploading(false);
    }
  };
  // Open the modal to update a specific report
  const openModal = (report) => {
    setSelectedReport(report); // Store the full report object
    setModalOpen(true); // Open the regular modal
  };

  // Open the modal to update a report's segment
  const openSegmentModal = (report) => {
    setSelectedReport(report); // Store the full report object
    setSegmentModalOpen(true); // Open the segment update modal
  };

  const handleSearchChange = (event) => {
    const term = event.target.value.toLowerCase();
    const filtered = reports.filter((report) =>
      report.reportTitle.toLowerCase().includes(term)
    );
    setFilteredReports(filtered);
    setSearchTerm(term);
  };

  const handleSort = () => {
    const sortedReports = [...filteredReports].sort((a, b) => {
      if (sortOrder === "asc") {
        return a.reportTitle.localeCompare(b.reportTitle);
      } else {
        return b.reportTitle.localeCompare(a.reportTitle);
      }
    });
    setFilteredReports(sortedReports);
    setSortOrder(sortOrder === "asc" ? "desc" : "asc"); // Toggle sort order
  };

  const handleEditClick = (report) => {
    // Navigate to the Edit Report page with the report ID
    navigate(`/admin/report/edit`, { state: report });
  };

  // Handle Delete button click
  const openDeleteModal = (report) => {
    setReportToDelete(report); // Store the report to delete
    setDeleteModalOpen(true); // Open the delete confirmation modal
  };

  const paginate = (reports) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return reports.slice(startIndex, endIndex);
  };

  const handleNextPage = () => {
    if (currentPage < Math.ceil(filteredReports.length / itemsPerPage)) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };

  // Handle confirmation of deletion
  const handleConfirmDelete = async () => {
    if (!reportToDelete) return;

    try {
      await axiosClient.delete(`/reports/delete/${reportToDelete._id}`);
      setReports((prev) =>
        prev.filter((report) => report._id !== reportToDelete._id)
      );
      setFilteredReports((prev) =>
        prev.filter((report) => report._id !== reportToDelete._id)
      );
      setMessage("Report deleted successfully.");
    } catch (error) {
      console.error("Error deleting report:", error);
      setMessage("Failed to delete the report. Please try again.");
    } finally {
      setDeleteModalOpen(false); // Close the modal
      setReportToDelete(null); // Clear the selected report
    }
  };

  // Cancel deletion
  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setReportToDelete(null);
  };

  const handleUpdateTitleClick = () => setModalOpen(true);

  const handleTitleUpdate = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      // Make an API call to update all report titles
      const response = await axiosClient.put("/reports/update-report-titles", {
        suffix: newTitle, // Send the title suffix entered by the user
      });

      console.log("API Response:", response.data);

      // Simulate success handling
      alert("Titles updated successfully!");
      setModalOpen(false);
    } catch (error) {
      console.error(
        "Error updating titles:",
        error.response?.data || error.message
      );
      alert("Failed to update titles. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <SideBar />
      {/* <Navbar /> */}

      <div className="flex justify-end mb-4 ml-25 mt-5">
        <button
          onClick={handleUpdateTitleClick}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none"
        >
          {/* <FontAwesomeIcon icon={faEdit} className="mr-2" /> */}
          Update Title
        </button>
        <div className="relative flex" style={{ width: "20rem" }}>
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search reports..."
            className="border rounded w-full py-2 pl-10 pr-3 text-gray-700 focus:outline-none focus:shadow-outline"
          />
          <FontAwesomeIcon
            icon={faSearch}
            className="absolute left-3 top-2.5 text-gray-500"
          />
        </div>
      </div>
      <div className="p-8" style={{ paddingLeft: "17rem" }}>
        {/* Heading and Sort button in a row */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold">All Reports</h1>
          <button
            onClick={handleSort}
            className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none flex items-center"
          >
            <FontAwesomeIcon
              icon={sortOrder === "asc" ? faArrowUpAZ : faArrowUpZA}
              className="mr-2"
            />
            Sort
          </button>
        </div>

        {/* Reports list */}
        <div className="flex flex-col justify-flex-start min-h-screen bg-gray-100 p-6 w-full">
          {loading ? (
            <p className="text-center">Loading...</p>
          ) : filteredReports.length === 0 ? (
            <p className="text-center text-gray-500">No reports found.</p>
          ) : (
            <ul className="space-y-3">
              {paginate(filteredReports).map((report) => (
                <li
                  key={report._id} // Use unique _id as key
                  className="flex justify-between items-center border-b pb-2"
                >
                  <span className="text-gray-800">{report.reportTitle}</span>
                  <div className="flex space-x-2">
                    {/* Edit Button */}
                    <button
                      onClick={() => handleEditClick(report)}
                      className="bg-yellow-500 hover:bg-yellow-700 text-white text-sm font-bold py-1 px-3 rounded focus:outline-none flex items-center"
                    >
                      <FontAwesomeIcon icon={faEdit} className="mr-2" />
                      Edit
                    </button>
                    {/* Delete Button */}
                    <button
                      onClick={() => openDeleteModal(report)}
                      className="bg-red-500 hover:bg-red-700 text-white text-sm font-bold py-1 px-3 rounded focus:outline-none flex items-center"
                    >
                      <FontAwesomeIcon icon={faEdit} className="mr-2" />
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="flex justify-center items-center mt-4">
        <button
          onClick={handlePrevPage}
          disabled={currentPage === 1}
          className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none mr-2"
        >
          Previous
        </button>
        <span>
          Page {currentPage} of{" "}
          {Math.ceil(filteredReports.length / itemsPerPage)}
        </span>
        <button
          onClick={handleNextPage}
          disabled={
            currentPage === Math.ceil(filteredReports.length / itemsPerPage)
          }
          className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none ml-2"
        >
          Next
        </button>
      </div>
      {/* Regular Report Update Modal */}
      {modalOpen && selectedReport && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-md w-full max-w-lg">
            <h3 className="text-lg font-bold mb-4 text-gray-800">
              Update {selectedReport.reportTitle}
            </h3>
            <form onSubmit={handleUpdate}>
              <div className="mb-4">
                <label
                  className="block text-gray-700 text-sm font-bold mb-2"
                  htmlFor="file"
                >
                  Upload File
                </label>
                <input
                  type="file"
                  id="file"
                  accept=".xlsx, .xls"
                  onChange={handleFileChange}
                  className="border rounded w-full py-2 px-3 text-gray-700 focus:outline-none"
                />
              </div>
              <div className="flex justify-end gap-2">
                <a
                  href="/sampleFile/singlegloblereport.xlsx"
                  download="single report data.xlsx"
                  className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none"
                >
                  <FontAwesomeIcon icon={faFileDownload} className="mr-2" />
                  Sample Report
                </a>
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="bg-gray-500 hover:bg-gray-700 text-white text-sm font-bold py-2 px-4 rounded focus:outline-none"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className="bg-blue-500 hover:bg-blue-700 text-white text-sm font-bold py-2 px-4 rounded focus:outline-none"
                >
                  {uploading ? "Uploading..." : "Upload"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Segment Update Modal */}
      {segmentModalOpen && selectedReport && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-md w-full max-w-lg">
            <h3 className="text-lg font-bold mb-4 text-gray-800">
              Update Segment for {selectedReport.reportTitle}
            </h3>
            <form onSubmit={handleSegmentUpdate}>
              <div className="mb-4">
                <label
                  className="block text-gray-700 text-sm font-bold mb-2"
                  htmlFor="file"
                >
                  Upload File
                </label>
                <input
                  type="file"
                  id="file"
                  accept=".xlsx, .xls"
                  onChange={handleFileChange}
                  className="border rounded w-full py-2 px-3 text-gray-700 focus:outline-none"
                />
              </div>
              <div className="flex justify-end gap-2">
                <a
                  href="/sampleFile/newbook.xlsx"
                  download="Sample_Report.xlsx"
                  className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none"
                >
                  <FontAwesomeIcon icon={faFileDownload} className="mr-2" />
                  Sample Report
                </a>
                <button
                  type="button"
                  onClick={() => setSegmentModalOpen(false)}
                  className="bg-gray-500 hover:bg-gray-700 text-white text-sm font-bold py-2 px-4 rounded focus:outline-none"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className="bg-blue-500 hover:bg-blue-700 text-white text-sm font-bold py-2 px-4 rounded focus:outline-none"
                >
                  {uploading ? "Uploading..." : "Upload"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {deleteModalOpen && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-md w-full max-w-sm">
            <h3 className="text-lg font-bold mb-4 text-gray-800">
              Confirm Delete
            </h3>
            <p className="mb-4">
              Are you sure you want to delete the report{" "}
              <strong>{reportToDelete?.reportTitle}</strong>?
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={handleCancelDelete}
                className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded focus:outline-none"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Update Title Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-md w-full max-w-md">
            <h3 className="text-lg font-bold mb-4 text-gray-800">
              Update Report Titles
            </h3>
            <form onSubmit={handleTitleUpdate}>
              <div className="mb-4">
                <label
                  className="block text-gray-700 text-sm font-bold mb-2"
                  htmlFor="title"
                >
                  New Title Suffix
                </label>
                <input
                  type="text"
                  id="title"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="Enter title suffix..."
                  className="border rounded w-full py-2 px-3 text-gray-700 focus:outline-none"
                  required
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none"
                >
                  {uploading ? "Updating..." : "Update"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Editreport;
